import { parse as parseQueryString, stringify } from 'query-string'
import { assocPath, flatten, identity } from 'ramda'

export type QueryStringParameters = Record<string, unknown>

export interface StateToQueryParamsParser<S, Q = QueryStringParameters> {
  serialize: (input: S) => Q
}

export interface QueryParamsToStateParser<S, Q = QueryStringParameters> {
  parse: (input: Q) => S
}

export type Parser<S, Q = QueryStringParameters> = StateToQueryParamsParser<S, Q> & QueryParamsToStateParser<S, Q>

interface Dependencies<S, Q extends object> {
  parser: StateToQueryParamsParser<S, Q> | QueryParamsToStateParser<S, Q> | Parser<S, Q>
}

export interface QueryStringSerializer<T = Record<string, unknown>> {
  serialize: (params: T) => string
  deserialize: (queryString: string) => T
}

function createIdentityParser<T, Q extends object = QueryStringParameters>(): Parser<T, Q> {
  return {
    serialize: identity as Parser<T, Q>['serialize'],
    parse: identity as Parser<T, Q>['parse'],
  }
}

function arrayToQueryString<Q extends object>(obj: Q) {
  return flatten(
    Object.entries(obj).map(([key, value]) => {
      const serializedValues = serializeArrayValues(value as string[][])
      return serializedValues.map(v => `${key}${v}`)
    }),
  ).join('&')
}

function serializeArrayValues(arr: string[][]): string[] {
  const result: string[] = []
  for (let i = 0; i < arr.length; i++) {
    if (!Array.isArray(arr[i])) {
      result.push(`[${i}]=${String(arr[i])}`)
      continue
    }
    for (let j = 0; j < arr[i].length; j++) {
      result.push(`[${i}][${j}]=${String(arr[i][j])}`)
    }
  }
  return result
}

function separateArrayValues(values: Record<string, unknown>) {
  return Object.entries(values).reduce(
    ([accArrayValues, accOtherValues], [key, value]) => {
      if (Array.isArray(value)) {
        return [{ ...accArrayValues, [key]: value }, accOtherValues]
      }
      return [accArrayValues, { ...accOtherValues, [key]: value }]
    },
    [{}, {}],
  )
}

export function createQueryStringSerializer<T, Q extends object = QueryStringParameters>(
  dependencies: Partial<Dependencies<T, Q>> = {},
) {
  const {
    parser = createIdentityParser<T, Q>(),
  } = dependencies

  const serialize = (values: T) => {
    if (!('serialize' in parser)) {
      throw new Error('Parser does not implement "serialize"')
    }

    const serializedValues = parser.serialize(values)

    // Filter out keys with null or undefined values
    const filteredValues = Object.entries(serializedValues)
      .reduce<Record<string, unknown>>((acc, [key, value]) => {
        if (value !== null && value !== undefined) {
          acc[key] = value
        }
        return acc
      }, {})

    const [arrayValues, otherValues] = separateArrayValues(filteredValues)

    return [arrayToQueryString(arrayValues), stringify(otherValues)].filter(Boolean).join('&')
  }

  function deserialize(input: string) {
    if (!('parse' in parser)) {
      throw new Error('Parser does not implement "parse"')
    }

    const parsedInput = parseQueryString(input)
    const parameters = Object.entries(parsedInput).reduce<Q>((acc, [key, value]: [string, unknown]) => {
      const [name, ...unfilteredPath] = key.split(/\[|\]\[|\]$/)

      const path = unfilteredPath.filter(Boolean).map(n => Number.parseInt(n, 10))

      return assocPath([name, ...path], value, acc)
    }, {} as Q)

    return parser.parse(parameters)
  }

  return {
    serialize,
    deserialize,
  }
}
